import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["submitButton"]
  static values = { streamUrl: String }

  connect() {
    this.isRunning = false
    this.abortController = null
    this.streamedResponse = ""
    this.streamedReasoning = ""
    this.lastStatusMessage = ""
    this.progressPercent = 0
    this.tokenTickCount = 0
    this.cleanupLegacyEstimateDatalistListener()

    if (this.hasSubmitButtonTarget) {
      this.submitButtonOriginalText = this.buttonText(this.submitButtonTarget)
    }
  }

  disconnect() {
    if (this.abortController) this.abortController.abort()
    if (this.streamingRenderTimer) clearTimeout(this.streamingRenderTimer)
  }

  async submit(event) {
    this.cancelTurboSubmission(event)
    if (this.isRunning) return

    const form = this.resolveFormFromEvent(event)
    if (!(form instanceof HTMLFormElement)) return

    this.beginRun()

    try {
      const response = await fetch(this.streamEndpoint(form), {
        method: "POST",
        body: new FormData(form),
        headers: {
          Accept: "application/x-ndjson; charset=utf-8",
          "X-CSRF-Token": this.csrfToken()
        },
        credentials: "same-origin",
        signal: this.abortController.signal
      })

      if (!response.ok) {
        throw new Error(`Stream request failed (${response.status})`)
      }

      if (!response.body) {
        throw new Error("Stream response body was empty")
      }

      await this.consumeNdjson(response.body)
    } catch (error) {
      if (error.name === "AbortError") return
      this.renderStatusAlert("danger", `AI analysis failed: ${this.escapeHtml(error.message)}`)
    } finally {
      this.finishRun(form)
    }
  }

  beginRun() {
    this.isRunning = true
    this.abortController = new AbortController()
    this.streamedResponse = ""
    this.streamedReasoning = ""
    this.lastStatusMessage = ""
    this.progressPercent = 5
    this.tokenTickCount = 0
    this.setSubmitButtonState(true)
    this.resetStreamPanels()
    this.renderStatusAlert("info", "Preparing analysis context...")
  }

  finishRun(form) {
    this.isRunning = false
    this.abortController = null
    this.setSubmitButtonState(false)
    this.restoreSelect22State(form)
    this.reinitializeSelect22(form)
  }

  async consumeNdjson(streamBody) {
    const reader = streamBody.getReader()
    const decoder = new TextDecoder()
    let buffer = ""

    while (true) {
      const { value, done } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      buffer = this.consumeBufferLines(buffer)
    }

    const trailing = buffer.trim()
    if (trailing.length > 0) this.processPayloadLine(trailing)
  }

  consumeBufferLines(buffer) {
    let newlineIndex = buffer.indexOf("\n")
    while (newlineIndex >= 0) {
      const line = buffer.slice(0, newlineIndex).trim()
      if (line.length > 0) this.processPayloadLine(line)
      buffer = buffer.slice(newlineIndex + 1)
      newlineIndex = buffer.indexOf("\n")
    }
    return buffer
  }

  processPayloadLine(line) {
    let payload
    try {
      payload = JSON.parse(line)
    } catch (_error) {
      return
    }

    const eventType = (payload.type || "").toString()

    if (eventType === "status") {
      const message = String(payload.message || "")
      this.lastStatusMessage = message
      this.bumpProgressTo(this.progressForStatus(message))
      this.renderStatusAlert("info", this.escapeHtml(message))
      return
    }

    if (eventType === "token") {
      const piece = String(payload.content || "")
      if (piece.length === 0) return
      this.streamedResponse += piece
      this.bumpProgressFromToken(piece)
      this.tokenTickCount += 1
      this.lastStatusMessage = "Receiving streamed response..."
      this.renderChunkCounter()
      this.renderStatusAlert("info", this.escapeHtml(this.lastStatusMessage))
      this.appendResponseChunk(piece)
      return
    }

    if (eventType === "reasoning") {
      const piece = String(payload.content || "")
      if (piece.length === 0) return
      this.streamedReasoning += piece
      this.appendReasoningChunk(piece)
      return
    }

    if (eventType === "heartbeat") {
      this.bumpProgressFromHeartbeat()
      const statusText = this.lastStatusMessage || "Running AI analysis..."
      this.renderStatusAlert("info", this.escapeHtml(statusText))
      return
    }

    if (eventType === "done") {
      this.bumpProgressTo(100)
      const streamed = String(this.streamedResponse || "")
      const doneResponse = String(payload.response || "")
      const doneReasoning = String(payload.reasoning || "")
      const finalResponse = doneResponse.trim().length > 0 ? doneResponse : streamed
      this.streamedResponse = finalResponse
      if (doneReasoning.trim().length > 0) this.streamedReasoning = doneReasoning
      this.renderReasoning(this.streamedReasoning)
      this.renderFinalResponse(finalResponse, { streaming: false })
      this.renderStatusAlert("success", "Analysis complete.")
      return
    }

    if (eventType === "error") {
      this.bumpProgressTo(100)
      this.renderStatusAlert("danger", this.escapeHtml(String(payload.message || "")))
    }
  }

  renderFinalResponse(responseText, { streaming = false } = {}) {
    const content = document.getElementById("ai_insights_stream_response_content")
    if (!content) return

    const formattedHtml = this.renderStructuredResponse(String(responseText || ""), { streaming })
    content.classList.remove("ai-insights-response-placeholder")
    content.innerHTML = formattedHtml
    this.liveResponseTextNode = null
  }

  resetStreamPanels() {
    const content = document.getElementById("ai_insights_stream_response_content")
    if (content) {
      content.classList.add("ai-insights-response-placeholder")
      content.innerHTML = "<p class=\"mb-0\">Waiting for response tokens...</p>"
    }
    const reasoningPanel = document.getElementById("ai_insights_stream_reasoning_panel")
    const reasoningText = document.getElementById("ai_insights_stream_reasoning_text")
    if (reasoningPanel) reasoningPanel.classList.add("d-none")
    if (reasoningText) reasoningText.textContent = ""
    this.liveResponseTextNode = null
    this.liveReasoningTextNode = null
    this.renderChunkCounter()
  }

  renderStatusAlert(level, messageHtml) {
    const percentage = Math.max(0, Math.min(100, Math.round(this.progressPercent)))
    const barTone = this.progressBarTone(level)
    const animatedClass = level === "info" && percentage < 100 ? "progress-bar-striped progress-bar-animated" : ""

    const panel = document.getElementById("ai_insights_stream_status")
    const message = document.getElementById("ai_insights_stream_status_message")
    const percent = document.getElementById("ai_insights_stream_percent")
    const bar = document.getElementById("ai_insights_stream_progress")
    if (!panel || !message || !percent || !bar) return

    panel.classList.remove("d-none")
    message.innerHTML = messageHtml
    percent.textContent = `${percentage}%`
    bar.style.width = `${percentage}%`
    bar.setAttribute("aria-valuenow", String(percentage))
    bar.classList.remove("bg-success", "bg-danger", "bg-warning", "progress-bar-striped", "progress-bar-animated")
    bar.classList.add(barTone)
    if (animatedClass) bar.classList.add("progress-bar-striped", "progress-bar-animated")
  }

  renderChunkCounter() {
    const counter = document.getElementById("ai_insights_stream_chunk_counter")
    if (!counter) return
    const label = this.tokenTickCount === 1 ? "chunk" : "chunks"
    counter.textContent = `${this.tokenTickCount.toLocaleString()} ${label}`
  }

  appendResponseChunk(piece) {
    const content = document.getElementById("ai_insights_stream_response_content")
    if (!content) return

    if (!this.liveResponseTextNode) {
      content.classList.remove("ai-insights-response-placeholder")
      content.replaceChildren()
      const pre = document.createElement("pre")
      pre.className = "mb-0 text-wrap"
      pre.style.whiteSpace = "pre-wrap"
      this.liveResponseTextNode = document.createTextNode("")
      pre.append(this.liveResponseTextNode)
      content.append(pre)
    }
    this.liveResponseTextNode.appendData(String(piece || ""))
  }

  appendReasoningChunk(piece) {
    const panel = document.getElementById("ai_insights_stream_reasoning_panel")
    const text = document.getElementById("ai_insights_stream_reasoning_text")
    if (!panel || !text) return

    panel.classList.remove("d-none")
    if (!this.liveReasoningTextNode) {
      text.textContent = ""
      this.liveReasoningTextNode = document.createTextNode("")
      text.append(this.liveReasoningTextNode)
    }
    this.liveReasoningTextNode.appendData(String(piece || ""))
  }

  renderReasoning(reasoningText) {
    const panel = document.getElementById("ai_insights_stream_reasoning_panel")
    const text = document.getElementById("ai_insights_stream_reasoning_text")
    if (!panel || !text) return

    const reasoning = String(reasoningText || "")
    panel.classList.toggle("d-none", reasoning.trim().length === 0)
    text.textContent = reasoning
    this.liveReasoningTextNode = null
  }

  progressBarTone(level) {
    switch (level) {
      case "success":
        return "bg-success"
      case "danger":
        return "bg-danger"
      case "warning":
        return "bg-warning"
      default:
        return "bg-warning"
    }
  }

  progressForStatus(message) {
    const text = String(message || "").toLowerCase()
    if (text.includes("preparing analysis context")) return 20
    if (text.includes("running ai analysis")) return 45
    if (text.includes("generating final response")) return 85
    if (text.includes("chunk")) return 75
    return 55
  }

  bumpProgressTo(value) {
    const next = Number(value)
    if (!Number.isFinite(next)) return
    this.progressPercent = Math.max(this.progressPercent, Math.min(100, next))
  }

  bumpProgressFromToken(piece) {
    if (this.progressPercent >= 95) return

    const length = String(piece || "").length
    const increment = Math.max(0.25, Math.min(1.5, length / 160))
    this.progressPercent = Math.min(95, this.progressPercent + increment)
  }

  bumpProgressFromHeartbeat() {
    if (this.progressPercent >= 94) return

    const increment = this.tokenTickCount > 0 ? 0.25 : 0.9
    this.progressPercent = Math.min(94, this.progressPercent + increment)
  }

  setSubmitButtonState(isBusy) {
    if (!this.hasSubmitButtonTarget) return

    this.submitButtonTarget.disabled = isBusy
    const label = isBusy ? "Running Analysis..." : this.submitButtonOriginalText

    if (this.submitButtonTarget.tagName === "INPUT") {
      this.submitButtonTarget.value = label
    } else {
      this.submitButtonTarget.textContent = label
    }
  }

  streamEndpoint(form) {
    if (this.hasStreamUrlValue && this.streamUrlValue.length > 0) return this.streamUrlValue
    return form.action
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }

  buttonText(button) {
    if (button.tagName === "INPUT") return button.value
    return button.textContent?.trim() || "Run Analysis"
  }

  escapeHtml(value) {
    return value
      .toString()
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;")
  }

  cancelTurboSubmission(event) {
    if (!event) return

    if (typeof event.preventDefault === "function") event.preventDefault()
    if (typeof event.stopPropagation === "function") event.stopPropagation()

    const formSubmission = event.detail?.formSubmission
    if (formSubmission && typeof formSubmission.stop === "function") {
      formSubmission.stop()
    }
  }

  resolveFormFromEvent(event) {
    if (event?.target instanceof HTMLFormElement) return event.target
    if (event?.currentTarget instanceof HTMLFormElement) return event.currentTarget
    if (this.element instanceof HTMLFormElement) return this.element
    return null
  }

  restoreSelect22State(form) {
    if (!(form instanceof HTMLFormElement)) return

    const select22 = this.application?.getControllerForElementAndIdentifier(form, "select22")
    if (!select22) return

    if (typeof select22.restoreFieldState === "function") {
      select22.restoreFieldState()
    }

    if (typeof select22.refreshToggleButtons === "function") {
      select22.refreshToggleButtons()
    }
  }

  reinitializeSelect22(form) {
    if (!(form instanceof HTMLFormElement)) return

    const select22 = this.application?.getControllerForElementAndIdentifier(form, "select22")
    if (select22 && typeof select22.reinitializeAfterStream === "function") {
      select22.reinitializeAfterStream()
      return
    }

    this.reinitializeSelect2Fallback(form)
  }

  reinitializeSelect2Fallback(form) {
    const $ = window.jQuery || window.$
    if (!($ && typeof $.fn?.select2 === "function")) return

    form.querySelectorAll('select[data-select22-target="field"]').forEach((field) => {
      const $field = $(field)
      if ($field.data("select2")) return

      $field.select2({
        width: "100%",
        closeOnSelect: false,
        placeholder: field.dataset.placeholder || "Select options",
        allowClear: false
      })
      $field.trigger("change")
    })
  }

  renderStructuredResponse(rawText, { streaming = false } = {}) {
    const normalized = this.normalizeStructuredMarkdown(rawText, { streaming })
    if (normalized.length === 0) return "<p>No response returned from model.</p>"

    const fragments = normalized.split(/(```[\s\S]*?```)/)
    const html = fragments.map((fragment) => {
      if (fragment.startsWith("```") && fragment.endsWith("```")) {
        return this.renderCodeBlock(fragment)
      }
      return this.renderTextBlocks(fragment)
    }).join("")

    return html.length > 0 ? html : "<p>No response returned from model.</p>"
  }

  normalizeStructuredMarkdown(rawText, { streaming = false } = {}) {
    let text = String(rawText || "").replace(/\r\n?/g, "\n")
    text = this.stripReasoningArtifacts(text, { streaming })
    text = text.replace(/^\s*#\s*$/gm, "")
    if (!streaming) text = this.splitInlinePipeRows(text)
    text = this.normalizeTabSeparatedTables(text)
    text = text.replace(/\t/g, "  ")
    text = text.replace(/[ \t]+\n/g, "\n")
    text = this.normalizeMergedMarkdownBoundaries(text)
    text = this.promoteSectionHeadings(text)
    if (!streaming) {
      text = this.normalizeMarkdownTables(text)
      text = this.repairAndFormatMarkdownTables(text)
    }
    text = text.replace(/\n{3,}/g, "\n\n")
    text = this.ensureMarkdownScaffold(text)

    return text.trim()
  }

  normalizeMergedMarkdownBoundaries(text) {
    let normalized = String(text || "")
    normalized = normalized.replace(/^[ \t]*[•●▪◦]\s+/gm, "- ")
    normalized = normalized.replace(/([^\n])\s*(#{2,6}\s+)/g, "$1\n\n$2")
    normalized = normalized.replace(/(^|\n)([A-Z][A-Za-z0-9/&()' ]{2,60})\s*-\s+(?=[A-Za-z0-9*])/g, "$1$2\n- ")
    normalized = normalized.replace(/([.!?])\s*-\s+(?=[A-Za-z*])/g, "$1\n- ")
    normalized = normalized.replace(/([.!?])\s*(\d+\.\s+)/g, "$1\n$2")
    normalized = normalized.replace(/:\s*-\s+(?=[A-Za-z*])/g, ":\n- ")
    normalized = normalized.replace(/([A-Za-z])(\d+(?:\.\d+)?%)/g, "$1 $2")
    normalized = normalized.replace(/:(\d+(?:\.\d+)?%)/g, ": $1")
    normalized = normalized.replace(/\b(January|February|March|April|May|June|July|August|September|October|November|December|through|from|to|before|after)(\d{4})\b/gi, "$1 $2")
    return normalized
  }

  promoteSectionHeadings(text) {
    const sectionNames = [
      "Executive Summary",
      "Core Metrics",
      "Approved Estimate Value Trends Over Time",
      "Approved Estimate Frequency Over Time",
      "Estimate Type Breakdown",
      "Implications & Next Actions",
      "Implications and Next Actions",
      "Observations",
      "Risks and Data Gaps",
      "Detailed Action Plan",
      "Follow-Up Analysis Questions",
      "Requested Analysis",
      "Key Findings",
      "Recommendations",
      "Conclusion"
    ]

    return String(text || "")
      .split("\n")
      .flatMap((line) => {
        const trimmed = line.trim()
        if (!trimmed || /^(?:#{1,6}\s|[-*+]\s|\d+\.\s|>|```|\|)/.test(trimmed)) return [line]

        const sectionName = sectionNames.find((name) => trimmed.toLowerCase().startsWith(name.toLowerCase()))
        if (!sectionName) return [line]

        const remainder = trimmed.slice(sectionName.length).replace(/^\s*[:\-–—]?\s*/, "").trim()
        return remainder.length > 0 ? [`## ${sectionName}`, "", remainder] : [`## ${sectionName}`]
      })
      .join("\n")
  }

  repairAndFormatMarkdownTables(text) {
    const lines = String(text || "").split("\n")
    const output = []
    const separatorPattern = /^\s*\|?\s*:?-{3,}:?\s*(?:\|\s*:?-{3,}:?\s*)+\|?\s*$/
    let index = 0

    while (index < lines.length) {
      const headerLine = lines[index]
      const separatorLine = lines[index + 1]
      if (!headerLine?.includes("|") || !separatorPattern.test(separatorLine || "")) {
        output.push(headerLine)
        index += 1
        continue
      }

      const headers = this.parseMarkdownTableRow(headerLine)
      const columnCount = Math.max(headers.length, 1)
      const rows = []
      index += 2

      while (index < lines.length && lines[index].includes("|")) {
        const cells = this.parseMarkdownTableRow(lines[index])
        const populated = cells.filter((cell) => String(cell || "").trim().length > 0)
        const nextCells = index + 1 < lines.length && lines[index + 1].includes("|")
          ? this.parseMarkdownTableRow(lines[index + 1])
          : null
        const nextPopulated = nextCells?.filter((cell) => String(cell || "").trim().length > 0) || []

        if (populated.length === 1 && nextPopulated.length === columnCount - 1) {
          rows.push([populated[0], ...nextPopulated])
          index += 2
          continue
        }

        rows.push(cells)
        index += 1
      }

      output.push(`| ${headers.join(" | ")} |`)
      output.push(`| ${headers.map(() => "---").join(" | ")} |`)
      rows.forEach((cells) => {
        const normalized = cells.slice(0, columnCount)
        while (normalized.length < columnCount) normalized.push("")
        const formatted = normalized.map((cell, cellIndex) => this.formatTableCell(cell, headers[cellIndex]))
        output.push(`| ${formatted.join(" | ")} |`)
      })
    }

    return output.join("\n")
  }

  formatTableCell(value, header) {
    const original = String(value ?? "").trim()
    const label = String(header ?? "").toLowerCase()
    const numericText = original.replace(/,/g, "")
    if (!original || !/^-?\d+(?:\.\d+)?$/.test(numericText)) return original

    const number = Number(numericText)
    if (!Number.isFinite(number)) return original

    if (/(share|percent|percentage|%)/.test(label)) {
      return `${number.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`
    }
    if (/(value|total|cost|revenue|profit|amount|price)/.test(label) && !/(count|hour)/.test(label)) {
      return `$${number.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    }
    if (/count/.test(label)) {
      return number.toLocaleString("en-US", { maximumFractionDigits: 0 })
    }
    if (/hour/.test(label)) {
      return number.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    }

    return original
  }

  splitInlinePipeRows(text) {
    let normalized = String(text || "")
    // Separate prose prefix from table rows when rows were merged inline.
    normalized = normalized.replace(
      /^([^\n|]+?)\s*(\|(?:[^|\n]*\|){2,})$/gm,
      (_match, prefix, row) => `${String(prefix || "").trim()}\n${String(row || "").trim()}`
    )
    // Split adjacent markdown rows that were merged on one line.
    normalized = normalized.replace(
      /(\|(?:[^|\n]*\|){2,})\s+(?=\|(?:[^|\n]*\|){2,})/g,
      "$1\n"
    )
    // If a table row starts mid-line, move it to its own line.
    normalized = normalized.replace(/([^\n])\s+(\|(?:[^|\n]*\|){2,})/g, "$1\n$2")
    return normalized
  }

  normalizeTabSeparatedTables(text) {
    const lines = String(text || "").split("\n")
    const output = []
    let index = 0

    while (index < lines.length) {
      let line = String(lines[index] || "")

      if (/^\s*#{1,6}[^\t]*\t+/.test(line)) {
        const [heading, ...rest] = line.split(/\t+/)
        output.push(String(heading || "").trimEnd())
        line = rest.join("\t")
      }

      if (line.includes("\t")) {
        const block = [line]
        index += 1
        while (index < lines.length && String(lines[index] || "").includes("\t")) {
          block.push(String(lines[index] || ""))
          index += 1
        }

        output.push(...this.tabBlockToMarkdown(block))
        continue
      }

      output.push(line)
      index += 1
    }

    return output.join("\n")
  }

  tabBlockToMarkdown(lines) {
    const rows = Array.from(lines || [])
      .map((line) => String(line || "").split(/\t+/).map((cell) => cell.trim()).filter((cell) => cell.length > 0))
      .filter((cells) => cells.length >= 2)

    if (rows.length === 0) return Array.from(lines || [])

    const columnCount = Math.max(...rows.map((cells) => cells.length))
    if (!Number.isFinite(columnCount) || columnCount < 2) return Array.from(lines || [])

    const normalizedRows = rows.map((cells) => {
      const padded = cells.slice(0, columnCount)
      while (padded.length < columnCount) padded.push("")
      return padded
    })

    const separatorIndex = normalizedRows.length > 1 && normalizedRows[1].every((cell) => this.separatorCell(cell)) ? 1 : null
    const tableLines = []

    normalizedRows.forEach((cells, rowIndex) => {
      if (separatorIndex === rowIndex) return

      tableLines.push(`| ${cells.map((cell) => this.escapePipeCell(cell)).join(" | ")} |`)
      if (rowIndex === 0) tableLines.push(`| ${Array.from({ length: columnCount }, () => "---").join(" | ")} |`)
    })

    return tableLines
  }

  separatorCell(value) {
    return /^:?-{2,}:?$/.test(String(value || "").replaceAll("|", "").trim())
  }

  escapePipeCell(value) {
    return String(value || "").replaceAll("|", "\\|")
  }

  ensureMarkdownScaffold(text) {
    const normalized = String(text || "").trim()
    if (normalized.length === 0) return normalized

    const markdownSignal = /(^|\n)\s*(#{1,6}\s+\S+|[-*]\s+\S+|\d+\.\s+\S+|\|.+\|)/m
    if (markdownSignal.test(normalized)) return normalized

    return `## Response\n\n${normalized}`
  }

  normalizeMarkdownTables(text) {
    const lines = String(text || "").split("\n")
    const output = []
    let index = 0

    while (index < lines.length) {
      const rowCandidate = this.extractTableRowCandidate(lines[index])
      if (rowCandidate) {
        const tableRows = []
        if (rowCandidate.prefix.length > 0) output.push(rowCandidate.prefix)
        tableRows.push(rowCandidate.row)
        index += 1

        while (index < lines.length) {
          const candidate = this.extractTableRowCandidate(lines[index])
          if (!candidate || candidate.prefix.length > 0) break
          tableRows.push(candidate.row)
          index += 1
        }

        const secondRow = String(tableRows[1] || "")
        const secondIsSeparator = secondRow.replaceAll("|", "").trim().match(/^[:\-\s]+$/)
        if (tableRows.length >= 2 && !secondIsSeparator) {
          const headerCols = String(tableRows[0] || "")
            .split("|")
            .map((cell) => cell.trim())
            .filter((cell) => cell.length > 0)
          const separator = `| ${headerCols.map(() => "---").join(" | ")} |`
          output.push(tableRows[0])
          output.push(separator)
          output.push(...tableRows.slice(1))
        } else {
          output.push(...tableRows)
        }
        continue
      }

      output.push(String(lines[index] || ""))
      index += 1
    }

    return output.join("\n")
  }

  extractTableRowCandidate(line) {
    const raw = String(line || "")
    const trimmed = raw.trim()
    if (trimmed.length === 0) return null
    if ((trimmed.match(/\|/g) || []).length < 2) return null

    if (trimmed.startsWith("|")) {
      return { prefix: "", row: trimmed }
    }

    const firstPipe = trimmed.indexOf("|")
    if (firstPipe <= 0) return null

    const prefix = trimmed.slice(0, firstPipe).trim()
    const row = trimmed.slice(firstPipe).trim()
    if ((row.match(/\|/g) || []).length < 2) return null

    return { prefix, row }
  }

  renderCodeBlock(fragment) {
    const code = fragment.replace(/^```[^\n]*\n?/, "").replace(/\n?```$/, "")
    return `<pre><code>${this.escapeHtml(code)}</code></pre>`
  }

  renderTextBlocks(text) {
    if (String(text || "").trim().length === 0) return ""

    const blocks = String(text)
      .split(/\n{2,}/)
      .map((block) => block.trim())
      .filter((block) => block.length > 0)

    return blocks.map((block) => this.renderBlock(block)).join("")
  }

  renderBlock(block) {
    const lines = String(block)
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.length > 0)

    if (lines.length === 0) return ""

    if (this.markdownHeadingLine(lines[0])) {
      const headingHtml = this.renderHeadingLine(lines[0])
      const remaining = lines.slice(1)
      if (remaining.length === 0) return headingHtml
      return `${headingHtml}${this.renderTextBlocks(remaining.join("\n"))}`
    }

    if (this.markdownTable(lines)) return this.renderMarkdownTable(lines)

    if (this.unorderedList(lines)) {
      const items = lines
        .map((line) => `<li>${this.formatInlineMarkdown(line.replace(/^[-*]\s+/, ""))}</li>`)
        .join("")
      return `<ul>${items}</ul>`
    }

    if (this.orderedList(lines)) {
      const items = lines
        .map((line) => `<li>${this.formatInlineMarkdown(line.replace(/^\d+\.\s+/, ""))}</li>`)
        .join("")
      return `<ol>${items}</ol>`
    }

    if (this.blockquote(lines)) {
      const quoteLines = lines.map((line) => line.replace(/^>\s?/, ""))
      const quote = this.formatInlineMarkdown(quoteLines.join("\n")).replace(/\n/g, "<br>")
      return `<blockquote><p>${quote}</p></blockquote>`
    }

    if (this.horizontalRule(lines)) return "<hr>"

    if (this.csvLikeBlock(lines)) {
      return `<pre><code>${this.escapeHtml(lines.join("\n"))}</code></pre>`
    }

    return `<p>${this.formatInlineMarkdown(lines.join("\n")).replace(/\n/g, "<br>")}</p>`
  }

  unorderedList(lines) {
    return lines.every((line) => /^[-*]\s+/.test(line))
  }

  orderedList(lines) {
    return lines.every((line) => /^\d+\.\s+/.test(line))
  }

  blockquote(lines) {
    return lines.every((line) => /^>\s?/.test(line))
  }

  horizontalRule(lines) {
    return lines.length === 1 && /^-{3,}$/.test(lines[0])
  }

  markdownHeadingLine(line) {
    return /^#{1,6}\s+\S+/.test(String(line || ""))
  }

  renderHeadingLine(line) {
    const match = String(line || "").match(/^(#{1,6})\s+(.+)$/)
    if (!match) return `<p>${this.formatInlineMarkdown(String(line || ""))}</p>`

    const level = Math.min(4, match[1].length + 1)
    const text = String(match[2] || "").trim()
    return `<h${level}>${this.formatInlineMarkdown(text)}</h${level}>`
  }

  markdownTable(lines) {
    if (lines.length < 2) return false
    const header = String(lines[0] || "")
    const separator = String(lines[1] || "")
    if (!header.includes("|")) return false

    const separatorWithoutPipes = separator.replaceAll("|", "").trim()
    return /^[:\-\s]+$/.test(separatorWithoutPipes)
  }

  renderMarkdownTable(lines) {
    const headerCells = this.parseMarkdownTableRow(lines[0])
    if (headerCells.length === 0) {
      return `<p>${this.formatInlineMarkdown(lines.join(" "))}</p>`
    }

    const columnCount = headerCells.length
    const bodyRows = lines.slice(2)
      .filter((line) => String(line).includes("|"))
      .map((line) => {
        const row = this.parseMarkdownTableRow(line).slice(0, columnCount)
        while (row.length < columnCount) row.push("")
        return row
      })

    const headerHtml = headerCells.map((cell) => `<th>${this.formatInlineMarkdown(cell)}</th>`).join("")
    const bodyHtml = bodyRows.map((row) => {
      const cells = row.map((cell) => `<td>${this.formatInlineMarkdown(cell)}</td>`).join("")
      return `<tr>${cells}</tr>`
    }).join("")

    return `<div class="ai-insights-table-wrap"><table class="ai-insights-table"><thead><tr>${headerHtml}</tr></thead><tbody>${bodyHtml}</tbody></table></div>`
  }

  parseMarkdownTableRow(line) {
    const normalized = String(line || "").trim().replace(/^\|/, "").replace(/\|$/, "")
    return normalized.split("|").map((cell) => cell.trim())
  }

  csvLikeBlock(lines) {
    if (lines.length < 2) return false
    const commaRichCount = lines.filter((line) => String(line).includes(",")).length
    return commaRichCount >= 2
  }

  formatInlineMarkdown(text) {
    let escaped = this.escapeHtml(String(text || ""))
    escaped = escaped.replace(/`([^`\n]+)`/g, "<code>$1</code>")
    escaped = escaped.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    escaped = escaped.replace(/\*([^*\n]+)\*/g, "<em>$1</em>")
    return escaped
  }

  stripReasoningArtifacts(text, { streaming = false } = {}) {
    let cleaned = String(text || "")
    if (streaming) {
      // While streaming, keep reasoning content visible but remove raw tags.
      return cleaned.replace(/<\/?think(?:\s[^>]*)?>/gi, "")
    }

    cleaned = cleaned.replace(/<think(?:\s[^>]*)?>[\s\S]*?<\/think>/gi, "")
    cleaned = cleaned.replace(/<think(?:\s[^>]*)?>[\s\S]*$/gi, "")
    cleaned = cleaned.replace(/<\/?think(?:\s[^>]*)?>/gi, "")
    return cleaned
  }

  cleanupLegacyEstimateDatalistListener() {
    const legacyHandler = window.updateCategoryDatalist
    if (typeof legacyHandler !== "function") return

    document.removeEventListener("DOMContentLoaded", legacyHandler)
    document.removeEventListener("turbo:load", legacyHandler)
    document.removeEventListener("turbo:render", legacyHandler)

    window.updateCategoryDatalist = function safeUpdateCategoryDatalist() {
      const dataList = document.getElementById("categories")
      if (!dataList) return
      dataList.innerHTML = ""
    }
  }
}
